import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import { App } from './App';
import { ConfigProvider } from './config/ConfigProvider';
import { AuthProvider } from './auth/AuthProvider';
import { ErrorBoundary } from './app/ErrorBoundary';
import { initTheme } from './lib/theme';

// Re-applies the theme (theme-init.js already did before paint) and follows OS changes
// while "System" is selected.
initTheme();

/**
 * Startup.
 *
 * The nesting order is the startup sequence, and it is not interchangeable:
 *
 * 1. `ConfigProvider` fetches and validates the runtime configuration document.
 *    Nothing inside it renders until that succeeds, because the identity provider's
 *    coordinates and the API endpoint both come from it.
 * 2. `AuthProvider` picks oidc or local from those values and resolves the session.
 * 3. `App` mounts the routes — the public pay page open, everything else behind the
 *    authentication guard.
 *
 * `StrictMode` is on deliberately. It double-invokes effects in development, which is
 * how the one-time authorization-code exchange and the provider teardowns get
 * exercised on every mount rather than failing in production the first time a remount
 * happens.
 */
const container = document.getElementById('root');
if (!container) {
  throw new Error('The application root element is missing from index.html.');
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <ConfigProvider>
        <AuthProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </ConfigProvider>
    </ErrorBoundary>
  </StrictMode>,
);
